import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { submitReport } from "../utils/blocks";
import { IoClose } from "react-icons/io5";
import Toast from "./Toast";
import { useFocusTrap } from "../utils/focusTrap";

// Reasons are tailored per target — a comment can't be a "fake account",
// and a design report cares about stolen/copyright content that doesn't
// apply anywhere else.
const REASONS_BY_TYPE = {
  user: [
    "Fake account",
    "Impersonation",
    "Spam",
    "Harassment or bullying",
    "Hate speech or symbols",
    "Scam or fraud",
    "Inappropriate content",
    "Other"
  ],
  comment: [
    "Spam",
    "Harassment or bullying",
    "Hate speech or symbols",
    "Misinformation",
    "Inappropriate content",
    "Other"
  ],
  design: [
    "Stolen design or copyright violation",
    "Spam",
    "Scam or fraud",
    "Inappropriate content",
    "Other"
  ]
};

const DEFAULT_REASONS = [
  "Spam",
  "Harassment or bullying",
  "Inappropriate content",
  "Other"
];

const OTHER_DETAILS_LIMIT = 300;

export default function ReportModal({ reporterId, targetType, targetId, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [toast, setToast] = useState("");
  // "Other" alone gives moderators nothing to act on — this switches the
  // sheet to a short free-text box instead of submitting immediately.
  const [otherDetails, setOtherDetails] = useState(null); // null = not on this screen; string = the draft text
  const reasons = REASONS_BY_TYPE[targetType] || DEFAULT_REASONS;
  // See LikeButton.jsx for why this ref exists alongside `submitting` state —
  // the reports table's unique constraint makes a double-fire here harmless
  // (the second call just gets 23505 and shows "already reported"), but the
  // guard keeps this consistent with the pattern used everywhere else.
  const submittingRef = useRef(false);
  const sheetRef = useRef(null);
  useFocusTrap(sheetRef, true);

  // Every other overlay in the app (FollowListModal, ImageCropModal,
  // ImageLightbox, NotificationBell, OnboardingModal, QRCodeModal) locks
  // background scroll while open — this one didn't.
  useEffect(() => {
    const unlockScroll = lockBodyScroll();

    function onKeyDown(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  function selectReason(r) {
    if (r === "Other") {
      setOtherDetails("");
      return;
    }
    handleReport(r);
  }

  function submitOther() {
    const details = otherDetails.trim();
    handleReport(details ? `Other — ${details}` : "Other");
  }

  async function handleReport(reason) {
    if (submitting || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    const { error } = await submitReport({ reporterId, targetType, targetId, reason });

    setSubmitting(false);
    submittingRef.current = false;

    if (error) {
      console.log("REPORT ERROR:", error);
      // 23505 = unique_violation — the DB-level guard against reporting the
      // same thing more than once (see reports_unique_report constraint).
      setToast(
        error.code === "23505"
          ? "You've already reported this."
          : "Couldn't submit report. Try again."
      );
      return;
    }

    setDone(true);
    setTimeout(() => onClose?.(), 1100);
  }

  return createPortal(
    <div style={backdrop} onClick={onClose}>
      <div style={sheet} ref={sheetRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Report" onClick={e => e.stopPropagation()}>
        <div style={header}>
          <strong style={{ fontSize: 15 }}>Report</strong>
          <button onClick={onClose} style={closeBtn} aria-label="Close">
            <IoClose />
          </button>
        </div>

        {done ? (
          <p style={doneText}>Reported. Thanks for letting us know.</p>
        ) : otherDetails !== null ? (
          <div style={otherWrap}>
            <p style={subText}>Tell us a bit more (optional)</p>
            <textarea
              autoFocus
              value={otherDetails}
              maxLength={OTHER_DETAILS_LIMIT}
              onChange={e => setOtherDetails(e.target.value)}
              placeholder="What's going on with this?"
              style={otherTextarea}
              disabled={submitting}
            />
            <div style={counter}>{otherDetails.length}/{OTHER_DETAILS_LIMIT}</div>
            <div style={otherActions}>
              <button style={backBtn} onClick={() => setOtherDetails(null)} disabled={submitting}>
                Back
              </button>
              <button style={submitBtn} onClick={submitOther} disabled={submitting}>
                {submitting ? "Submitting…" : "Submit report"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p style={subText}>Why are you reporting this?</p>
            {reasons.map(r => (
              <button
                key={r}
                style={reasonBtn}
                disabled={submitting}
                onClick={() => selectReason(r)}
              >
                {r}
              </button>
            ))}
          </>
        )}
      </div>

      {toast && <Toast text={toast} onClose={() => setToast("")} />}
    </div>,
    document.body
  );
}

const backdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(20, 14, 8, 0.55)",
  zIndex: 1200,
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center"
};

const sheet = {
  background: "var(--bg-surface)",
  width: "100%",
  maxWidth: 420,
  borderRadius: "18px 18px 0 0",
  padding: "0 0 16px",
  paddingBottom: "calc(16px + env(safe-area-inset-bottom))"
};

const header = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  padding: "14px 16px",
  borderBottom: "1px solid var(--border-soft)"
};

const closeBtn = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  background: "none",
  border: "none",
  fontSize: 22,
  color: "var(--text-body)",
  cursor: "pointer",
  padding: 4
};

const subText = {
  fontSize: 12.5,
  color: "var(--text-muted)",
  margin: "12px 16px 4px"
};

const reasonBtn = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "13px 16px",
  border: "none",
  borderBottom: "1px solid var(--border-soft)",
  background: "none",
  fontSize: 14,
  color: "var(--text-heading)",
  cursor: "pointer"
};

const doneText = {
  padding: "24px 16px",
  textAlign: "center",
  fontSize: 14,
  color: "var(--accent-success)",
  fontWeight: 600
};

const otherWrap = {
  padding: "4px 16px 16px"
};

const otherTextarea = {
  width: "100%",
  minHeight: 90,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-heading)",
  fontSize: 14,
  fontFamily: "inherit",
  resize: "vertical",
  boxSizing: "border-box"
};

const counter = {
  fontSize: 11,
  color: "var(--text-muted)",
  textAlign: "right",
  marginTop: 4
};

const otherActions = {
  display: "flex",
  gap: 10,
  marginTop: 14
};

const backBtn = {
  flex: 1,
  padding: 12,
  borderRadius: 12,
  border: "1.5px solid var(--border-input)",
  background: "none",
  color: "var(--text-secondary)",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer"
};

const submitBtn = {
  flex: 2,
  padding: 12,
  borderRadius: 12,
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer"
};
